import { SVGProps, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Button, ButtonVariant } from '../../../_core/button/button.component';
import { ITemplateBaseDetails } from '../../../../lib/types';
import { showError } from '../../../../lib/utils';
import { useDeleteTemplate, useRestoreTemplate } from '../../../domains/template/template.service';
import { IPersonalCompanyProps } from '../../../_core/core.types';
import { ArrowPathIcon, DocumentDuplicateIcon, EyeIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/20/solid';
import { IOnClickProps } from '../../../_core/click.types';
import { useConfirm } from '../../../_core/confirm/confirm.utils';

interface IButtonListOption extends IOnClickProps {
  Element: React.ComponentType<SVGProps<SVGSVGElement>>;
  text: string;
  tooltip?: string;
  variant?: ButtonVariant;
}

export interface ITemplateActionProps {
  index: number;
  isGlobalAdmin?: boolean;
  onCreateRequest?: () => void;
  onEdit: () => void;
  onPreview: () => void;
  template: ITemplateBaseDetails;
}

export interface ITemplateActionButtonProps extends ITemplateActionProps, IPersonalCompanyProps {}

export const TemplateActionButton: React.FC<ITemplateActionButtonProps> = ({
  index,
  isGlobalAdmin,
  onCreateRequest,
  onEdit,
  onPreview,
  template
}) => {
  const { ConfirmationDialog: PermanentlyDeleteConfirmationDialog, confirm: confirmPermanent } = useConfirm({
    title: 'Are you sure you want to permanently delete this template?',
    message: 'This action cannot be undone. Consider archiving instead.'
  });

  const { ConfirmationDialog: DeleteOptionDialog, confirm: confirmDeleteOption } = useConfirm({
    title: 'Would you like to archive or delete?',
    cancelTitle: 'Archive',
    confirmTitle: 'Delete'
  });

  const { deleteTemplate, loading: deletingTemplate } = useDeleteTemplate();
  const { restoreTemplate, loading: restoringTemplate } = useRestoreTemplate();

  const previewTemplateAction: IButtonListOption = useMemo(
    () => ({
      Element: EyeIcon as React.ComponentType,
      onClick: onPreview,
      text: 'Preview',
      variant: 'secondary'
    }),
    [onPreview]
  );

  const createRequestFromTemplateAction: IButtonListOption = useMemo(
    () => ({
      Element: DocumentDuplicateIcon as React.ComponentType,
      onClick: onCreateRequest,
      text: 'Create Request',
      variant: 'secondary'
    }),
    [onCreateRequest]
  );

  const editTemplateAction: IButtonListOption = useMemo(
    () => ({ Element: PencilSquareIcon as React.ComponentType, onClick: onEdit, text: 'Edit', variant: 'secondary' }),
    [onEdit]
  );

  const onRestoreTemplate = useCallback(async () => {
    if (template.deletedAt)
      try {
        await restoreTemplate(template);
        toast.success(`Restored Template`);
      } catch (err) {
        showError('Unable to restore template', err as Error);
      }
  }, [template, restoreTemplate]);

  const restoreTemplateAction: IButtonListOption = useMemo(
    () => ({ Element: ArrowPathIcon as React.ComponentType, onClick: onRestoreTemplate, text: 'Restore' }),
    [onRestoreTemplate]
  );

  const callDelete = useCallback(
    async (permanent?: boolean) => {
      try {
        await deleteTemplate({ ...template, permanent });
        toast.success(`${permanent ? 'Deleted' : 'Archived'} template`);
      } catch (err) {
        showError(`Failed to ${permanent ? 'delete' : 'archive'} template`, err as Error);
      }
    },
    [deleteTemplate, template]
  );

  const onDeleteTemplate = useCallback(async () => {
    confirmDeleteOption().then((shouldDelete) => {
      if (shouldDelete)
        return confirmPermanent().then((confirmed) => {
          if (confirmed) callDelete(true);
        });
      else return callDelete();
    });
  }, [callDelete, confirmDeleteOption, confirmPermanent]);

  const deleteTemplateAction: IButtonListOption = useMemo(
    () => ({ Element: TrashIcon as React.ComponentType, onClick: onDeleteTemplate, text: 'Delete' }),
    [onDeleteTemplate]
  );

  const options = useMemo(() => {
    const newOptions: IButtonListOption[] = [previewTemplateAction];
    if (onCreateRequest) newOptions.push(createRequestFromTemplateAction);

    if (template.company || isGlobalAdmin)
      newOptions.push(editTemplateAction, template.deletedAt ? restoreTemplateAction : deleteTemplateAction);

    return newOptions;
  }, [
    previewTemplateAction,
    onCreateRequest,
    createRequestFromTemplateAction,
    template.company,
    template.deletedAt,
    isGlobalAdmin,
    editTemplateAction,
    restoreTemplateAction,
    deleteTemplateAction
  ]);

  return (
    <>
      <div className="flex">
        {options.map((o) => (
          <Button
            slim
            key={`button-${index}-${o.text}`}
            icon={<o.Element height={20} width={20} />}
            loading={deletingTemplate || restoringTemplate}
            onClick={(e) => {
              if (o.onClick) {
                o.onClick(e);
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            size="fit"
            tooltip={o.text}
            variant={o.variant}
          />
        ))}
      </div>
      <DeleteOptionDialog />
      <PermanentlyDeleteConfirmationDialog />
    </>
  );
};
